/**
 * Push Notification Toast Component
 * Displays toast notifications when push messages arrive.
 */
import { useState, useEffect } from 'react';
import { X, Server, AlertCircle, CreditCard, MessageSquare, Info, Check, AlertTriangle } from 'lucide-react';

// Icon mapping
const typeIcons = {
  instance_status: Server,
  instance_created: Server,
  instance_deleted: Server,
  billing_alert: CreditCard,
  support_reply: MessageSquare,
  system_alert: AlertCircle,
  warning: AlertTriangle,
  error: X,
  success: Check,
  info: Info,
};

// Color mapping
const typeColors = {
  instance_status: 'border-blue-500/50 bg-blue-500/10',
  instance_created: 'border-emerald-500/50 bg-emerald-500/10',
  instance_deleted: 'border-red-500/50 bg-red-500/10',
  billing_alert: 'border-yellow-500/50 bg-yellow-500/10',
  support_reply: 'border-cyan-500/50 bg-cyan-500/10',
  system_alert: 'border-orange-500/50 bg-orange-500/10',
  warning: 'border-yellow-500/50 bg-yellow-500/10',
  error: 'border-red-500/50 bg-red-500/10',
  success: 'border-emerald-500/50 bg-emerald-500/10',
  info: 'border-blue-500/50 bg-blue-500/10',
};

const iconColors = {
  instance_status: 'text-blue-400',
  instance_created: 'text-emerald-400',
  instance_deleted: 'text-red-400',
  billing_alert: 'text-yellow-400',
  support_reply: 'text-cyan-400',
  system_alert: 'text-orange-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
  success: 'text-emerald-400',
  info: 'text-blue-400',
};

function ToastItem({ notification, onDismiss }) {
  const Icon = typeIcons[notification.type] || Info;
  const colorClass = typeColors[notification.type] || 'border-zinc-700 bg-zinc-900';
  const iconColor = iconColors[notification.type] || 'text-zinc-400';

  useEffect(() => {
    // Auto dismiss after 5 seconds
    const timer = setTimeout(() => {
      onDismiss(notification.id);
    }, 5000);
    return () => clearTimeout(timer);
  }, [notification.id, onDismiss]);

  return (
    <div
      data-testid="notification-toast"
      className={`flex items-start gap-3 p-4 rounded-sm border ${colorClass} backdrop-blur-sm shadow-lg animate-slide-in`}
      style={{
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      <div className={`flex-shrink-0 ${iconColor}`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{notification.title}</p>
        <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{notification.message}</p>
      </div>
      <button
        onClick={() => onDismiss(notification.id)}
        className="flex-shrink-0 text-zinc-500 hover:text-white transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default function NotificationToast() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    // Listen for push notification events
    const handlePushNotification = (event) => {
      const notification = event.detail;
      setToasts(prev => [...prev, notification].slice(-3)); // Keep max 3 toasts
    };

    window.addEventListener('push-notification', handlePushNotification);
    return () => window.removeEventListener('push-notification', handlePushNotification);
  }, []);

  const dismissToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {toasts.map(toast => (
        <ToastItem
          key={toast.id}
          notification={toast}
          onDismiss={dismissToast}
        />
      ))}
    </div>
  );
}
